import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Send, Sparkles, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Email Campaign composer for the Admin Dashboard.
 *
 * - Individual OR broadcast recipient (mirror of the Push composer)
 * - AI-generated markdown body using the customer's Kundli as RAG context
 * - Live HTML "poster" preview rendered by the backend
 * - Logs every send into email_campaigns for later analytics
 */
export default function EmailComposer({ customers }) {
  const [target, setTarget] = useState("all");
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [bodyMd, setBodyMd] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Book Consultation");
  const [ctaUrl, setCtaUrl] = useState("https://app.theastrofate.com/consultation");
  const [ctaLabel2, setCtaLabel2] = useState("Talk to AstroFate AI");
  const [ctaUrl2, setCtaUrl2] = useState("https://app.theastrofate.com/chat");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [ctxUser, setCtxUser] = useState(null);

  useEffect(() => {
    if (target === "all") { setCtxUser(null); return; }
    const c = (customers || []).find(x => x.id === target);
    setCtxUser(c || null);
  }, [target, customers]);

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim() || target === "all") return;
    setAiLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email/generate-ai`,
        { user_id: target, prompt: aiPrompt.trim() },
        { withCredentials: true }
      );
      if (data.markdown) {
        setBodyMd(data.markdown);
        if (!subject) setSubject("A personal note from your AstroFate astrologer");
        if (!heading) setHeading("Guidance from your Kundli ✦");
      }
    } catch (err) {
      alert(err.response?.data?.detail || "AI generation failed.");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!heading.trim() || !bodyMd.trim()) return;
    setPreviewLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email/preview`,
        {
          subject: subject || "AstroFate",
          heading, body_markdown: bodyMd,
          cta_label: ctaLabel || null, cta_url: ctaUrl || null,
          cta_label_2: ctaLabel2 || null, cta_url_2: ctaUrl2 || null,
        },
        { withCredentials: true }
      );
      setPreviewHtml(data.html || "");
    } catch (err) {
      alert(err.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !heading.trim() || !bodyMd.trim()) {
      setResult({ error: "Subject, heading and body are required." });
      return;
    }
    if (target === "all" && !window.confirm("Send this email to ALL registered users?")) return;
    setSending(true); setResult(null);
    try {
      const payload = {
        subject: subject.trim(),
        heading: heading.trim(),
        body_markdown: bodyMd.trim(),
        cta_label: ctaLabel.trim() || null,
        cta_url: ctaUrl.trim() || null,
        cta_label_2: ctaLabel2.trim() || null,
        cta_url_2: ctaUrl2.trim() || null,
      };
      if (target !== "all") payload.user_id = target;
      const { data } = await axios.post(`${API}/admin/email/send`, payload, { withCredentials: true });
      setResult({ ok: true, ...data });
      if (data.success_count) {
        setSubject(""); setHeading(""); setBodyMd(""); setAiPrompt("");
        // Keep the two default CTAs prefilled for the next campaign.
      }
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Failed to send campaign." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Mail size={20} style={{ color: "#FFD166" }} />
          <h2 className="font-cinzel" style={{ fontSize: "1.15rem", color: "#FFD166" }}>Email Campaign</h2>
        </div>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "4px" }}>
          Deliver beautifully-branded AstroFate emails — send to one customer or broadcast to all.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, 1fr)", gap: "18px", alignItems: "start" }}>
        <form onSubmit={handleSend} className="glass-card" style={{ padding: "24px" }}>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Recipient</label>
            <select data-testid="email-target-select" value={target} onChange={e => setTarget(e.target.value)} className="cosmic-input">
              <option value="all">All registered users</option>
              {(customers || []).slice(0, 500).map(c => (
                <option key={c.id} value={c.id}>{c.name || c.email} · {c.email}</option>
              ))}
            </select>
          </div>

          {target !== "all" && (
            <div style={{ background: "rgba(157,78,221,0.08)", border: "1px solid rgba(157,78,221,0.25)", borderRadius: "10px", padding: "14px", marginBottom: "18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#c4b5fd", fontSize: "0.8rem", fontWeight: 600, marginBottom: "8px" }}>
                <Sparkles size={14} /> Generate email body using {ctxUser?.name?.split(" ")[0] || "this customer"}&apos;s Kundli
              </div>
              <textarea data-testid="email-ai-prompt" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                placeholder="e.g. Marriage timing insights for the next 12 months"
                rows={2} className="cosmic-input" style={{ resize: "vertical", minHeight: "48px" }} />
              <button type="button" data-testid="email-ai-generate-btn" onClick={handleGenerateAI} disabled={aiLoading || !aiPrompt.trim()}
                className="btn-purple" style={{ marginTop: "8px", padding: "7px 14px", fontSize: "0.78rem" }}>
                {aiLoading ? "Consulting AI…" : "✦ Generate with Kundli context"}
              </button>
            </div>
          )}

          <div style={{ marginBottom: "14px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Subject line</label>
            <input data-testid="email-subject" type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="A note from your AstroFate astrologer" maxLength={140} className="cosmic-input" />
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Headline (poster hero)</label>
            <input data-testid="email-heading" type="text" value={heading} onChange={e => setHeading(e.target.value)}
              placeholder="Your ideal marriage window unveiled" maxLength={120} className="cosmic-input" />
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>
              Body (markdown supported — # heading, **bold**, - bullet)
            </label>
            <textarea data-testid="email-body" value={bodyMd} onChange={e => setBodyMd(e.target.value)}
              placeholder="## Your cosmic snapshot&#10;- Venus enters your 7th house on…&#10;- Rahu transits through…&#10;&#10;Book a consultation for a full plan."
              rows={10} className="cosmic-input" style={{ resize: "vertical", minHeight: "220px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
            <div>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>CTA #1 label (gold)</label>
              <input data-testid="email-cta-label" type="text" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)}
                placeholder="Book Consultation" maxLength={40} className="cosmic-input" />
            </div>
            <div>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>CTA #1 URL</label>
              <input data-testid="email-cta-url" type="text" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)}
                placeholder="https://app.theastrofate.com/consultation" className="cosmic-input" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "18px" }}>
            <div>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>CTA #2 label (purple)</label>
              <input data-testid="email-cta-label-2" type="text" value={ctaLabel2} onChange={e => setCtaLabel2(e.target.value)}
                placeholder="Talk to AstroFate AI" maxLength={40} className="cosmic-input" />
            </div>
            <div>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>CTA #2 URL</label>
              <input data-testid="email-cta-url-2" type="text" value={ctaUrl2} onChange={e => setCtaUrl2(e.target.value)}
                placeholder="https://app.theastrofate.com/chat" className="cosmic-input" />
            </div>
          </div>

          {result?.error && (
            <div data-testid="email-error" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px", color: "#fca5a5", fontSize: "0.85rem" }}>
              {result.error}
            </div>
          )}
          {result?.ok && (
            <div data-testid="email-success" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px", color: "#86efac", fontSize: "0.85rem" }}>
              Sent {result.success_count} email{result.success_count === 1 ? "" : "s"} of {result.recipients} attempted.
              {result.failure_count > 0 && ` · ${result.failure_count} failed.`}
            </div>
          )}

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button type="button" data-testid="email-preview-btn" onClick={handlePreview} disabled={previewLoading} className="btn-ghost" style={{ padding: "9px 16px", fontSize: "0.82rem" }}>
              {previewLoading ? "Rendering…" : "Preview poster"}
            </button>
            <button data-testid="email-send-btn" type="submit" disabled={sending} className="btn-gold" style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 20px" }}>
              <Send size={14} /> {sending ? "Sending…" : target === "all" ? "Send to All" : "Send Campaign"}
            </button>
          </div>
        </form>

        <div className="glass-card" style={{ padding: "14px", border: "1px solid rgba(255,209,102,0.15)", position: "sticky", top: "88px", maxHeight: "80vh", overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#FFD166", fontWeight: 700, fontSize: "0.86rem", marginBottom: "10px" }}>
            <Users size={14} /> Poster preview
          </div>
          {previewHtml ? (
            <iframe
              data-testid="email-preview-iframe"
              title="Email poster preview"
              srcDoc={previewHtml}
              style={{ width: "100%", height: "560px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", background: "#0c0018" }}
              sandbox=""
            />
          ) : (
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem", padding: "40px 12px", textAlign: "center", lineHeight: 1.6 }}>
              Fill in the subject, headline, and body — then hit <b style={{ color: "#FFD166" }}>Preview poster</b> to see exactly what customers will receive.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
